// "Share with…": how an item reaches a person from the People app, by how they
// connect. A person and a grant are linked by the grant's `from`, the person's
// handle (the same value a capture link already carries). Pure; the sheet is
// components/ShareWith.tsx.
import type { GrantResource } from '@/kinds/grant/apply'
import { isLive } from '@/kinds/grant/apply'
import { memberScope } from '@/kinds/grant/schema'
import type { PersonResource } from '@/kinds/person/apply'
import { handlesOf } from './people'

export type ShareKind = 'Task' | 'Project' | 'Page' | 'List' | 'Date'

export interface ShareItem {
  kind: ShareKind
  id: string
  title: string
  /** the item's workspace slug (a Date has none) */
  ws?: string
  /** a Date's day, said in words, since a Date has no link to send */
  when?: string
}

export type ShareRoute =
  /** a login to the item's suite: it is already in their app; they are pushed */
  | { how: 'app'; grant: GrantResource }
  /** they have an app, but not this item's suite (or the kind never reaches a member) */
  | { how: 'elsewhere'; grant: GrantResource; slugs: string[] }
  /** only a capture link: a note on their page, no push */
  | { how: 'link'; grant: GrantResource }
  | { how: 'none' }

const mine = (person: PersonResource, grants: GrantResource[], now: number) => {
  const handles = new Set(handlesOf(person))
  return grants.filter(
    (g) =>
      !g.deleted_at &&
      !g.spec.api &&
      !g.spec.paused &&
      !!g.spec.from &&
      handles.has(g.spec.from.toLowerCase()) &&
      isLive(g, now).ok,
  )
}

export const isMemberGrant = (g: GrantResource): boolean =>
  g.spec.scopes.length > 0 && g.spec.scopes.every((s) => memberScope(s) !== null)

/** How an item reaches this person: their app first, else their capture page, else not at all. */
export const routeFor = (
  person: PersonResource,
  grants: GrantResource[],
  item: ShareItem,
  now = Date.now(),
): ShareRoute => {
  const live = mine(person, grants, now)
  const apps = live.filter(isMemberGrant)
  // a member sees Tasks, Projects, Pages and Lists of her suites, never a Date
  const reaches =
    item.kind !== 'Date'
      ? apps.find((g) => g.spec.scopes.some((s) => memberScope(s)?.slug === item.ws))
      : undefined
  if (reaches) return { how: 'app', grant: reaches }
  const link = live.find((g) => g.spec.scopes.includes('capture'))
  if (link) return { how: 'link', grant: link }
  const first = apps[0]
  if (first) {
    return {
      how: 'elsewhere',
      grant: first,
      slugs: apps.flatMap((g) => g.spec.scopes.map((s) => memberScope(s)?.slug ?? '')).filter(Boolean),
    }
  }
  return { how: 'none' }
}

/** The scope of a read-only link to the item, or none (a Date has no link). */
export const linkScope = (item: ShareItem): string | null =>
  item.kind === 'Date' ? null : `${item.kind.toLowerCase()}:${item.id}:read`

export const NOTE_MAX = 280

/** The line left on a capture page: "<You> shared: <title>" and the link. */
export const sharedNote = (owner: string, item: ShareItem, url?: string): string => {
  const head = `${owner} shared: `
  const tail = url ? `\n${url}` : item.when ? ` (${item.when})` : ''
  const room = Math.max(8, NOTE_MAX - head.length - tail.length)
  const title = item.title.trim()
  return head + (title.length > room ? `${title.slice(0, room - 1)}…` : title) + tail
}

/** A link made for a note lasts a month. */
export const linkExpiry = (now = Date.now()): string => new Date(now + 30 * 86_400_000).toISOString()
